/**
 * Sticky Add to Cart Button
 * Shows a sticky Add to Cart button at the bottom of the screen on mobile
 * when the regular Add to Cart button is out of view.
 */

class StickyAddToCart {
  constructor() {
    console.log('StickyAddToCart constructor called');
    // Wait a moment for DOM to be fully ready
    setTimeout(() => this.initializeElements(), 100);
    
    // Set up a mutation observer to catch any new sticky bars that might be added to the DOM
    this.setupMutationObserver();
  }
  
  setupMutationObserver() {
    // Create an observer instance linked to a callback function
    this.domObserver = new MutationObserver(mutations => {
      // Check if any mutations added new sticky bars
      const hasNewStickyBars = mutations.some(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          // Convert nodelist to array and check if any new sticky bars were added
          return Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the node itself is a sticky bar
              if (node.classList && node.classList.contains('sticky-add-to-cart-bar')) {
                return true;
              }
              // Check if the node contains sticky bars
              const childStickyBars = node.querySelectorAll && node.querySelectorAll('.sticky-add-to-cart-bar');
              return childStickyBars && childStickyBars.length > 0;
            }
            return false;
          });
        }
        return false;
      });
      
      // If new sticky bars were added, clean up duplicates
      if (hasNewStickyBars) {
        console.log('New sticky bars detected in DOM. Cleaning up duplicates...');
        this.removeDuplicateStickyBars();
      }
    });
    
    // Start observing the document with the configured parameters
    this.domObserver.observe(document.body, { childList: true, subtree: true });
  }

  removeDuplicateStickyBars() {
    // Find all sticky bar elements
    const stickyBars = document.querySelectorAll('.sticky-add-to-cart-bar');
    console.log(`Found ${stickyBars.length} sticky bar elements in total`);
    
    if (stickyBars.length <= 1) {
      return false; // No duplicates to remove
    }
    
    // Determine which one to keep (prefer the one already in body, or the last one)
    let keepIndex = stickyBars.length - 1; // Default to keeping the last one
    for (let i = 0; i < stickyBars.length; i++) {
      // If we find one directly in the body, prefer to keep that one
      if (stickyBars[i].parentElement.tagName === 'BODY') {
        keepIndex = i;
        break;
      }
    }
    
    // Remove all but the one to keep
    for (let i = 0; i < stickyBars.length; i++) {
      if (i !== keepIndex) {
        console.log(`Removing duplicate sticky bar at index ${i}`);
        stickyBars[i].style.display = 'none';
        stickyBars[i].remove();
      }
    }
    
    return true; // Duplicates were removed
  }
  
  initializeElements() {
    // Clean up any duplicates first
    this.removeDuplicateStickyBars();
    
    // Find the sticky bar element (should be only one now)
    this.stickyBar = document.querySelector('.sticky-add-to-cart-bar');
    if (!this.stickyBar) {
      console.error('Sticky bar element not found');
      return;
    }
    
    // Ensure the sticky bar is positioned correctly in the DOM (move to body if needed)
    if (this.stickyBar.parentElement.tagName !== 'BODY') {
      console.log('Moving sticky bar to body for proper positioning');
      const stickyBarClone = this.stickyBar.cloneNode(true);
      document.body.appendChild(stickyBarClone);
      this.stickyBar.remove(); // Completely remove from DOM instead of just hiding
      this.stickyBar = stickyBarClone; // Use the one attached to body
    }
    
    // Find the original button - be more flexible in selection
    this.originalButton = document.querySelector('.product-form__submit') || 
                         document.querySelector('button[name="add"]');
    if (!this.originalButton) {
      console.error('Original add to cart button not found');
      return;
    }
    console.log('Original button found:', this.originalButton);
    
    // Find the sticky button
    this.stickyButton = this.stickyBar.querySelector('.sticky-add-to-cart-bar__button');
    if (!this.stickyButton) {
      console.error('Sticky button element not found within sticky bar');
      return;
    }
    
    // Get product form data
    this.productFormId = this.stickyBar.dataset.productFormId;
    this.productForm = document.getElementById(this.productFormId);
    if (!this.productForm) {
      console.log('Product form not found with id:', this.productFormId);
      // Try to find the form by other means
      this.productForm = document.querySelector('form[action*="/cart/add"]');
      console.log('Fallback product form found:', !!this.productForm);
    }
    
    // Ensure the sticky bar has the correct absolute positioning styles
    this.ensureCorrectStyles();
    
    // Set initial visibility state
    this.setInitialState();
    
    // Initialize events and observers
    this.init();
  }
  
  ensureCorrectStyles() {
    // Force the critical positioning styles directly on the element
    Object.assign(this.stickyBar.style, {
      position: 'fixed',
      bottom: '0',
      left: '0',
      width: '100%',
      zIndex: '999'
    });
  }

  setInitialState() {
    // Make sure the sticky bar is initially hidden but prepared for transitions
    this.stickyBar.style.transform = 'translateY(100%)';
    this.stickyBar.style.visibility = 'hidden';
    this.stickyBar.setAttribute('aria-hidden', 'true');
    
    // Force repaint to ensure transitions work properly
    void this.stickyBar.offsetWidth;
  }

  init() {
    // Proceed with initialization only if we have required elements
    if (!this.stickyBar || !this.originalButton || !this.stickyButton) {
      console.error('Missing required elements, cannot initialize sticky add to cart');
      return;
    }

    console.log('Initializing sticky add to cart');
    
    // Set up Intersection Observer to watch the original Add to Cart button
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.1 // trigger when 10% of the element is visible
    };

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), options);
    this.observer.observe(this.originalButton);
    console.log('Observer set up for original button');

    // Add click event to the sticky button
    this.stickyButton.addEventListener('click', this.handleStickyButtonClick.bind(this));
    
    // Add resize and scroll listeners
    window.addEventListener('resize', this.handleResize.bind(this));
    window.addEventListener('scroll', this.checkButtonVisibility.bind(this), { passive: true });
    
    // Periodically check for duplicates and ensure correct styles
    this.intervalCheck = setInterval(() => {
      this.removeDuplicateStickyBars();
      this.ensureCorrectStyles();
    }, 2000);
    
    // Do an initial check in case the button is already out of view on load
    setTimeout(() => this.checkButtonVisibility(), 500);
    
    // Set up a cleanup function for when the page is unloaded
    window.addEventListener('beforeunload', this.destroy.bind(this));
  }
  
  destroy() {
    // Clean up all event listeners and observers
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.domObserver) {
      this.domObserver.disconnect();
    }
    if (this.intervalCheck) {
      clearInterval(this.intervalCheck);
    }
    if (this.stickyButton) {
      this.stickyButton.removeEventListener('click', this.handleStickyButtonClick);
    }
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.checkButtonVisibility);
  }

  checkButtonVisibility() {
    if (window.innerWidth > 749) return; // Only for mobile
    
    // Get the position of the original button
    const rect = this.originalButton.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    // If button is not visible in viewport, show sticky bar
    if (rect.bottom < 0 || rect.top > windowHeight) {
      this.showStickyBar();
    } else {
      this.hideStickyBar();
    }
  }

  handleIntersection(entries) {
    if (window.innerWidth > 749) return; // Only for mobile
    
    // We're only observing one element
    const entry = entries[0];
    
    // If the original button is not visible, show the sticky bar
    if (!entry.isIntersecting) {
      this.showStickyBar();
    } else {
      this.hideStickyBar();
    }
  }

  showStickyBar() {
    if (!this.stickyBar.classList.contains('sticky-add-to-cart-bar--visible')) {
      console.log('Showing sticky bar');
      // Ensure correct positioning before showing
      this.ensureCorrectStyles();
      // Make element visible before applying transform for proper transition
      this.stickyBar.style.visibility = 'visible';
      this.stickyBar.style.display = 'block';
      // Force repaint
      void this.stickyBar.offsetWidth;
      // Apply visible class
      this.stickyBar.classList.add('sticky-add-to-cart-bar--visible');
      this.stickyBar.style.transform = 'translateY(0)';
      this.stickyBar.style.position = 'fixed';
      this.stickyBar.style.bottom = '0';
      this.stickyBar.setAttribute('aria-hidden', 'false');
      // Override any potential inline styles that might interfere
      this.stickyBar.style.position = 'fixed !important';
      this.stickyBar.style.bottom = '0 !important';
    }
  }

  hideStickyBar() {
    if (this.stickyBar.classList.contains('sticky-add-to-cart-bar--visible')) {
      console.log('Hiding sticky bar');
      this.stickyBar.classList.remove('sticky-add-to-cart-bar--visible');
      this.stickyBar.style.transform = 'translateY(100%)';
      this.stickyBar.setAttribute('aria-hidden', 'true');
      
      // Make sure position remains fixed while hiding (for smooth transition)
      this.stickyBar.style.position = 'fixed';
      this.stickyBar.style.bottom = '0';
      
      // Hide completely after transition completes
      setTimeout(() => {
        if (!this.stickyBar.classList.contains('sticky-add-to-cart-bar--visible')) {
          this.stickyBar.style.visibility = 'hidden';
        }
      }, 300); // Match the CSS transition duration
    }
  }

  handleStickyButtonClick(e) {
    e.preventDefault();
    console.log('Sticky button clicked');
    
    // Find the original submit button and click it
    if (this.productForm) {
      const submitButton = this.productForm.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.click();
        console.log('Original button clicked');
      } else {
        console.error('Submit button not found in product form');
      }
    } else {
      console.error('Product form not found');
    }
  }

  handleResize() {
    // Disable on desktop, enable on mobile
    if (window.innerWidth > 749) {
      this.hideStickyBar();
    } else {
      // Do a visibility check on resize for mobile
      this.checkButtonVisibility();
    }
  }
}

// Initialize when DOM content is loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM content loaded, initializing sticky add to cart');
  new StickyAddToCart();
});

// Also initialize when the page is fully loaded (including images)
window.addEventListener('load', () => {
  console.log('Window loaded, ensuring sticky add to cart is initialized');
  new StickyAddToCart();
});
